import { observable } from '@legendapp/state';
import { CompleteConfigurationV2 } from '@volkswagen-onehub/car-configuration-feature-service';
import { FormsServiceV1 } from '@volkswagen-onehub/forms-service';

import { errorMessage, info, warning } from '../../utils/errors';
import translate from '../../utils/translate';
import { fetchVictorCacheKey } from './nba/utils/fetch-victor-cache-key';
import { getBuildableConfiguration } from './nba/utils/get-buildable-config';

import { TrackingEventName, TrackingNBABInfo } from './tracking/types';

import { rootStore } from '../root-store';
import { featureAppStore } from './feature-app-store';
import { trackingStore } from './tracking-store';

enum NBA_ACTIONS {
	DCC = 'visualizer::dcc-transition', // Profile icon
	CONFIGURATOR = 'visualizer::configurator-transition', // Magnifier icon
	EXTERNAL = 'visualizer::external-transition' // Internet icon
}

enum TrackingCtaLinkName {
	Configure = 'configure',
	External = 'external',
	Internal = 'internal',
	TestDrive = 'testdrive'
}

const testDriveFormOptions = {
	faId: 'Forms',
	primaryForm: 'testdrive',
	sourceApplication: 'visualizer',
	userCloseable: false
} as const;

type OpenedFormHandle = {
	close?: () => void;
	isClosed?: () => boolean;
};

// TODO: remove this workaround once Forms/AEM layer state is cleaned up correctly by the service itself.
let openedTestDriveFormHandle: OpenedFormHandle | undefined;

// TODO: remove this workaround once reOpenForms no longer survives page reloads in AEM.
let isTestDriveFormCleanupRegistered = false;

function closeOpenedTestDriveForm() {
	if (!openedTestDriveFormHandle) return;

	if (openedTestDriveFormHandle.isClosed?.()) {
		openedTestDriveFormHandle = undefined;
		return;
	}

	openedTestDriveFormHandle.close?.();
	openedTestDriveFormHandle = undefined;
}

function ensureTestDriveFormCleanup() {
	if (typeof window === 'undefined' || isTestDriveFormCleanupRegistered) return;

	window.addEventListener('pagehide', () => {
		closeOpenedTestDriveForm();
	});

	isTestDriveFormCleanupRegistered = true;
}

function goToAnchor(anchorId: string) {
	const anchor = document.querySelector(anchorId) as HTMLDivElement;
	if (anchor) {
		setTimeout(() => {
			// We want to avoid header
			const yScrollMargin = 100;
			window.scrollTo(0, anchor.offsetTop - yScrollMargin);
		}, 100);
	} else {
		errorMessage('An anchor is configured as exitURL, which does not exist on the page');
	}
}

function exitToUrl(externalExitUrl?: string, trackingData?: TrackingNBABInfo) {
	const track = trackingStore.track;

	if (!externalExitUrl) {
		warning('Exit "url" is enabled but no exitURL is configured. Please verify the nba configuration.');
		return;
	}

	info(`Using following link ${externalExitUrl}`);

	const shouldExitToInternalAnchor = externalExitUrl.startsWith('#');
	const linkName = shouldExitToInternalAnchor ? TrackingCtaLinkName.Internal : TrackingCtaLinkName.External;

	track(TrackingEventName.VISUALIZER_NBA_CLICK, {
		contentId: `${linkName}: ${externalExitUrl}`,
		link: {
			url: externalExitUrl,
			name: linkName
		},
		content: trackingData
	});

	if (shouldExitToInternalAnchor) {
		goToAnchor(externalExitUrl);
		return;
	}
	window.location.assign(externalExitUrl);
}

async function exitToDCC(trackingData?: TrackingNBABInfo) {
	try {
		const featureServices = featureAppStore.featureServices.get();
		const formsService = featureServices?.['forms-service'] as FormsServiceV1 | undefined;

		if (formsService) {
			const nodeIdModelPreselect = rootStore.currentModelNodeId.get();
			const layerTitle = translate('global.testDriveFormTitle');
			const openFormOptions = nodeIdModelPreselect
				? {
						...testDriveFormOptions,
						layerTitle,
						nodeIdModelPreselect
					}
				: {
						...testDriveFormOptions,
						layerTitle
					};

			if (!nodeIdModelPreselect) {
				console.warn(
					'[WARN] nodeIdModelPreselect is undefined. Opening test drive form with the first step (model selection).'
				);
			}

			closeOpenedTestDriveForm();
			ensureTestDriveFormCleanup();

			const [formHandle, err] = formsService.openForm(openFormOptions);

			if (!err) {
				openedTestDriveFormHandle = formHandle;

				trackingStore.track(TrackingEventName.VISUALIZER_NBA_CLICK, {
					contentId: 'testdrive-form-layer',
					link: {
						url: '#reOpenForms',
						name: TrackingCtaLinkName.TestDrive
					},
					content: trackingData
				});
				info(
					`Opened test drive form in interaction layer${nodeIdModelPreselect ? ` for ${nodeIdModelPreselect}` : ''}.`
				);
				return;
			}

			formHandle?.close?.();
			warning(`Failed to open test drive form layer. Falling back to DCC navigation. ${err.message}`);
		}

		const track = trackingStore.track;
		const navigationService = featureServices?.['navigation-service'];

		if (!navigationService) {
			errorMessage('GFA Navigation Service not available. Please contact dev team');
			return;
		}

		const dccTarget = navigationService.navigateTo('dcc', {
			'dcc-feature-app': {
				pathname: '/configure'
			}
		});

		if (!dccTarget) {
			errorMessage('Unable to create DCC navigation target. Please contact dev team');
			return;
		}

		track(TrackingEventName.VISUALIZER_NBA_CLICK, {
			contentId: 'dcc-navigation',
			link: {
				url: dccTarget.createHref(),
				name: 'dcc'
			},
			content: trackingData
		});

		dccTarget.push();
	} catch (e) {
		errorMessage(`Couldn't navigate to DCC. Contact Dev team. \n ${e}`);
		throw e;
	}
}

async function exitToConfigurator(trackingData?: TrackingNBABInfo) {
	const track = trackingStore.track;
	const cacheKey = await fetchVictorCacheKey();
	const buildableConfiguration = await getBuildableConfiguration(cacheKey);
	if (!buildableConfiguration) return errorMessage('Failed to get buildable configuration. Contact Dev Team');

	const featureServices = featureAppStore.featureServices;

	const singleConfigFlowNav = featureServices['single-configuration-flow-navigator'];
	const showroomNavService = featureServices['showroom-navigation'];

	const singleFlowTarget = singleConfigFlowNav?.createConfigureNavigation(
		buildableConfiguration as CompleteConfigurationV2
	);

	// @TODO fix types
	const showroomTarget = showroomNavService?.createConfigureShowroomNavigation?.(
		JSON.stringify(buildableConfiguration)
	);

	if (singleConfigFlowNav && singleFlowTarget) {
		info(`Single Flow navigate to: ${buildableConfiguration}`);

		track(TrackingEventName.VISUALIZER_NBA_CLICK, {
			contentId: TrackingCtaLinkName.Configure,
			link: {
				url: singleFlowTarget.href,
				name: TrackingCtaLinkName.Configure
			},
			content: trackingData
		});

		singleFlowTarget.onClick();
		return;
	}

	if (showroomNavService && showroomTarget && singleFlowTarget) {
		info(`Showroom navigate to: ${buildableConfiguration}`);

		track(TrackingEventName.VISUALIZER_NBA_CLICK, {
			contentId: TrackingCtaLinkName.Configure,
			link: {
				url: singleFlowTarget.href,
				name: TrackingCtaLinkName.Configure
			},
			content: trackingData
		});

		// @ts-expect-error This will be debugged later
		showroomTarget.push();
		return;
	}

	warning(`Neither showroom nor single flow is available for configuration: ${JSON.stringify(buildableConfiguration)}`);
}

export const nbaStore = observable({
	isSubscribed: false,

	getUserInteractionService: () => {
		const featureServices = featureAppStore.featureServices.get();
		return featureServices?.['user-interaction-service'];
	},

	subscribeToDCCTransfer: () => {
		const service = nbaStore.getUserInteractionService();
		if (!service) return;
		service.register(NBA_ACTIONS.DCC, { action: () => exitToDCC() });
	},

	unsubscribeToDCCTransfer: () => {
		const service = nbaStore.getUserInteractionService();
		if (!service) return;
		service.unregister(NBA_ACTIONS.DCC);
	},

	subscribeToConfigureTransfer: () => {
		const service = nbaStore.getUserInteractionService();
		if (!service) return;
		service.register(NBA_ACTIONS.CONFIGURATOR, { action: () => exitToConfigurator() });
	},

	unsubscribeToConfigureTransfer: () => {
		const service = nbaStore.getUserInteractionService();
		if (!service) return;
		service.unregister(NBA_ACTIONS.CONFIGURATOR);
	},

	subscribeToExternalTransfer: () => {
		const service = nbaStore.getUserInteractionService();
		if (!service) return;
		service.register(NBA_ACTIONS.EXTERNAL, { action: () => exitToUrl() });
	},

	unsubscribeToExternalTransfer: () => {
		const service = nbaStore.getUserInteractionService();
		if (!service) return;
		service.unregister(NBA_ACTIONS.EXTERNAL);
	},

	subscribeToAllNBATransfers: () => {
		nbaStore.subscribeToConfigureTransfer();
		nbaStore.subscribeToDCCTransfer();
		nbaStore.subscribeToExternalTransfer();
		nbaStore.isSubscribed.set(true);
	},

	unsubscribeToAllNBATransfers: () => {
		nbaStore.unsubscribeToConfigureTransfer();
		nbaStore.unsubscribeToDCCTransfer();
		nbaStore.unsubscribeToExternalTransfer();
		nbaStore.isSubscribed.set(false);
	}
});
